/**
 * Provider-independent marketplace order addresses.
 *
 * Authorities (never substitute):
 *   Ship To  = marketplace order delivery address
 *   Bill To  = marketplace billing address
 *   From     = Tradivo fulfilment / business location (not stored here)
 *
 * Missing Ship To fails closed (null). Do not copy billing, warehouse, sender,
 * customer master, connector, or organisation addresses into Ship To.
 */

export type CanonicalOrderAddress = {
  name: string | null;
  company: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  country_code: string | null;
  phone: string | null;
  email: string | null;
};

export type CanonicalOrderAddresses = {
  shipping: CanonicalOrderAddress | null;
  billing: CanonicalOrderAddress | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s ? s : null;
}

function joinName(first: unknown, last: unknown, fallback?: unknown): string | null {
  const joined = [str(first), str(last)].filter(Boolean).join(" ").trim();
  if (joined) return joined;
  return str(fallback);
}

export function isUsableCanonicalAddress(value: unknown): boolean {
  const rec = asRecord(value);
  if (!rec) return false;
  if (str(rec.address_line1) || str(rec.address) || str(rec.street_1) || str(rec.line1)) return true;
  const city = str(rec.city);
  const post = str(rec.postal_code) || str(rec.zip) || str(rec.postcode);
  return Boolean(city && post);
}

export function canonicalOrderAddressOrNull(value: unknown): CanonicalOrderAddress | null {
  return isUsableCanonicalAddress(value) ? (value as CanonicalOrderAddress) : null;
}

function fromCanonicalFields(input: {
  name: string | null;
  company: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  country_code: string | null;
  phone: string | null;
  email: string | null;
}): CanonicalOrderAddress | null {
  const addr: CanonicalOrderAddress = {
    name: input.name,
    company: input.company,
    address_line1: input.address_line1,
    address_line2: input.address_line2,
    city: input.city,
    state: input.state,
    postal_code: input.postal_code,
    country: input.country,
    country_code: input.country_code,
    phone: input.phone,
    email: input.email,
  };
  return isUsableCanonicalAddress(addr) ? addr : null;
}

/** BigCommerce v2 order billing_address / shipping_addresses[] resource. */
export function mapBigCommerceV2Address(raw: unknown): CanonicalOrderAddress | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  return fromCanonicalFields({
    name: joinName(rec.first_name, rec.last_name, rec.name),
    company: str(rec.company),
    address_line1: str(rec.street_1) || str(rec.address_line1) || str(rec.address),
    address_line2: str(rec.street_2) || str(rec.address_line2),
    city: str(rec.city),
    state: str(rec.state) || str(rec.state_or_province),
    postal_code: str(rec.zip) || str(rec.postal_code) || str(rec.postcode),
    country: str(rec.country),
    country_code: str(rec.country_iso2) || str(rec.country_code),
    phone: str(rec.phone),
    email: str(rec.email),
  });
}

export function mapBigCommerceV2OrderAddresses(
  order: Record<string, unknown>,
  shippingAddresses: unknown[],
  opts?: { warehouseOrSender?: unknown },
): CanonicalOrderAddresses {
  void opts?.warehouseOrSender;
  const shippingList = Array.isArray(shippingAddresses) ? shippingAddresses : [];
  let shipping: CanonicalOrderAddress | null = null;
  for (const row of shippingList) {
    shipping = mapBigCommerceV2Address(row);
    if (shipping) break;
  }
  const billing = mapBigCommerceV2Address(order.billing_address);
  return { shipping, billing };
}

function mapEbayContactAddress(shipTo: Record<string, unknown>): CanonicalOrderAddress | null {
  const contact = asRecord(shipTo.contactAddress) ?? shipTo;
  const phoneObj = asRecord(shipTo.primaryPhone);
  return fromCanonicalFields({
    name: str(shipTo.fullName) || str(shipTo.name) || joinName(shipTo.firstName, shipTo.lastName),
    company: str(shipTo.companyName) || str(shipTo.company),
    address_line1: str(contact.addressLine1) || str(contact.address_line1),
    address_line2: str(contact.addressLine2) || str(contact.address_line2),
    city: str(contact.city),
    state: str(contact.stateOrProvince) || str(contact.state),
    postal_code: str(contact.postalCode) || str(contact.postal_code),
    country: str(contact.country) || str(contact.countryCode) || str(contact.country_code),
    country_code: str(contact.countryCode) || str(contact.country_code),
    phone: str(phoneObj?.phoneNumber) || str(shipTo.phone),
    email: str(shipTo.email),
  });
}

/**
 * eBay Sell Fulfillment: delivery is shippingStep.shipTo.
 * Buyer registration is not billing and is never used as Ship To or Bill To.
 * eBay typically has no separate billing address — billing stays null.
 */
export function mapEbayFulfillmentOrderAddresses(order: {
  fulfillmentStartInstructions?: Array<{ shippingStep?: { shipTo?: unknown } }>;
  buyer?: { buyerRegistrationAddress?: unknown };
}): CanonicalOrderAddresses {
  const shipTo = asRecord(order.fulfillmentStartInstructions?.[0]?.shippingStep?.shipTo);
  const shipping = shipTo ? mapEbayContactAddress(shipTo) : null;
  void order.buyer?.buyerRegistrationAddress;
  return { shipping, billing: null };
}

export function neverSubstituteOrderAddresses(args: {
  shipping: CanonicalOrderAddress | null;
  billing: CanonicalOrderAddress | null;
  sender?: unknown;
}): CanonicalOrderAddresses {
  void args.sender;
  return { shipping: args.shipping, billing: args.billing };
}
